// ABOUTME: Catalog-level owner of the product favorite stars: one toggle mutation and one cache watch for every card.
// ABOUTME: A family can mount hundreds of cards at once, so no card mounts an Apollo hook of its own.

import { gql, useApolloClient } from '@apollo/client';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
    IsFavoriteDocument,
    IsFavoriteQuery,
    IsFavoriteQueryVariables,
    useToggleFavoriteMutation,
} from '../../../user-favorites/graphql/userFavorites.generated';
import { ProductFavoriteTarget } from '../../utils/resolveProductFavoriteTarget';
import { productFavoriteKey, useCatalogFavorites } from './CatalogFavoritesContext';

interface CatalogProductFavoritesValue {
    /** The star of `target`: its `IsFavorite` cache entry when there is one, else the payload's flag. */
    isFavorite: (target: ProductFavoriteTarget, marked: boolean | undefined) => boolean;
    isToggling: (target: ProductFavoriteTarget) => boolean;
    /** Toggles the favorite with its snackbar feedback; `productId` is the card id the strip keys on. */
    toggle: (target: ProductFavoriteTarget, productId: string) => void;
    /** Puts the target under the cache watch while its card is mounted; returns the unregister. */
    register: (target: ProductFavoriteTarget) => () => void;
}

const CatalogProductFavoritesContext = createContext<CatalogProductFavoritesValue | null>(null);

const targetKey = ({ hierarchyId, nodeId }: ProductFavoriteTarget) =>
    JSON.stringify([hierarchyId, nodeId]);

/**
 * One query reading the `isFavorite` field of every target, aliased `f0`, `f1`, …. It addresses the
 * same cache entries as `IsFavorite`, so a single watch sees every write of the toggle mutation,
 * wherever in the session it was made.
 */
const buildIsFavoriteBatchQuery = (count: number) => {
    const indexes = Array.from({ length: count }, (_, index) => index);
    const variables = indexes.map(index => `$h${index}: String!, $n${index}: String!`).join(', ');
    const fields = indexes
        .map(index => `f${index}: isFavorite(hierarchyId: $h${index}, nodeId: $n${index})`)
        .join('\n');
    return gql(`query CatalogProductFavorites(${variables}) {\n${fields}\n}`);
};

const sameValues = (a: Record<string, boolean>, b: Record<string, boolean>) => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every(key => a[key] === b[key]);
};

/**
 * Serves the favorite stars of the catalog cards. The payload marks the favorites once; the
 * `IsFavorite` cache entries, written by every toggle of the session (the cards, the product detail
 * dialog, the configurator), win over those flags, as the cards' cache-only reads would.
 */
export function CatalogProductFavoritesProvider({ children }: { children: ReactNode }) {
    const { formatMessage } = useIntl();
    const client = useApolloClient();
    const [toggleMutation] = useToggleFavoriteMutation();
    // Present in the treeview catalog, whose favorites strip follows the stars.
    const catalogFavorites = useCatalogFavorites();
    const setStripFavorite = catalogFavorites?.setFavorite;

    const [cached, setCached] = useState<Record<string, boolean>>({});
    const [toggling, setToggling] = useState<Record<string, boolean>>({});
    // The mounted cards' targets with how many cards show each, kept out of state so that mounting
    // a family re-renders nothing but the watch below.
    const registered = useRef(new Map<string, { target: ProductFavoriteTarget; cards: number }>());
    const [registrationVersion, setRegistrationVersion] = useState(0);

    const register = useCallback((target: ProductFavoriteTarget) => {
        const key = targetKey(target);
        const entry = registered.current.get(key);
        if (entry) {
            entry.cards += 1;
        } else {
            registered.current.set(key, { target, cards: 1 });
            setRegistrationVersion(version => version + 1);
        }
        return () => {
            const current = registered.current.get(key);
            if (!current) return;
            current.cards -= 1;
            if (current.cards === 0) {
                registered.current.delete(key);
                setRegistrationVersion(version => version + 1);
            }
        };
    }, []);

    useEffect(() => {
        const targets = Array.from(registered.current.values(), entry => entry.target);
        if (!targets.length) {
            setCached(prev => (Object.keys(prev).length ? {} : prev));
            return undefined;
        }
        const query = buildIsFavoriteBatchQuery(targets.length);
        const variables = Object.fromEntries(
            targets.flatMap((target, index) => [
                [`h${index}`, target.hierarchyId],
                [`n${index}`, target.nodeId],
            ]),
        );
        const apply = (result: Record<string, unknown> | null | undefined) => {
            const next = Object.fromEntries(
                targets
                    .map((target, index) => [targetKey(target), result?.[`f${index}`]] as const)
                    .filter(
                        (pair): pair is readonly [string, boolean] => typeof pair[1] === 'boolean',
                    ),
            );
            setCached(prev => (sameValues(prev, next) ? prev : next));
        };
        const options = { query, variables, optimistic: true, returnPartialData: true };
        apply(client.cache.diff<Record<string, unknown>>(options).result);
        return client.cache.watch<Record<string, unknown>>({
            ...options,
            callback: diff => apply(diff.result),
        });
    }, [client, registrationVersion]);

    const toggle = useCallback(
        async (target: ProductFavoriteTarget, productId: string) => {
            const key = targetKey(target);
            const variables = { hierarchyId: target.hierarchyId, nodeId: target.nodeId };
            setToggling(prev => ({ ...prev, [key]: true }));
            try {
                const result = await toggleMutation({
                    variables,
                    update: (cache, { data: mutationData }) => {
                        if (!mutationData?.toggleFavorite) return;
                        cache.writeQuery({
                            query: IsFavoriteDocument,
                            variables,
                            data: { isFavorite: mutationData.toggleFavorite.isFavorite },
                        });
                    },
                });
                const toggled = result.data?.toggleFavorite;
                if (toggled) {
                    SnackBarUtils.success(
                        formatMessage({
                            id: 'user-favorites.toggle-success',
                            defaultMessage: 'Favorites updated',
                        }),
                    );
                    setStripFavorite?.(productFavoriteKey(productId), toggled.isFavorite);
                }
            } catch {
                SnackBarUtils.error(
                    formatMessage({
                        id: 'generic-error',
                        defaultMessage: 'An unexpected error occurred.',
                    }),
                );
            } finally {
                setToggling(({ [key]: _done, ...rest }) => rest);
            }
        },
        [toggleMutation, formatMessage, setStripFavorite],
    );

    // The watch starts in an effect, after the first render of a card: until it has reported, the
    // cache is read directly, so a cached value never shows the payload's flag for a frame.
    const readCached = useCallback(
        ({ hierarchyId, nodeId }: ProductFavoriteTarget) => {
            try {
                return (
                    client.cache.readQuery<IsFavoriteQuery, IsFavoriteQueryVariables>(
                        { query: IsFavoriteDocument, variables: { hierarchyId, nodeId } },
                        true,
                    )?.isFavorite ?? undefined
                );
            } catch {
                return undefined;
            }
        },
        [client],
    );
    const isFavorite = useCallback(
        (target: ProductFavoriteTarget, marked: boolean | undefined) => {
            const key = targetKey(target);
            const value = key in cached ? cached[key] : readCached(target);
            return value ?? marked === true;
        },
        [cached, readCached],
    );
    const isToggling = useCallback(
        (target: ProductFavoriteTarget) => !!toggling[targetKey(target)],
        [toggling],
    );

    const value = useMemo(
        () => ({ isFavorite, isToggling, toggle, register }),
        [isFavorite, isToggling, toggle, register],
    );
    return (
        <CatalogProductFavoritesContext.Provider value={value}>
            {children}
        </CatalogProductFavoritesContext.Provider>
    );
}

/** The catalog's product favorites, or null outside a catalog that serves them. */
export function useCatalogProductFavorites(): CatalogProductFavoritesValue | null {
    return useContext(CatalogProductFavoritesContext);
}
